import "./UniversalAppBuilderComponent.scss";
import { useParams, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState, useCallback } from "react";
import commonService from "@/services/common.service.ts";
import { motion } from "framer-motion";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import ChatScreenComponent from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/chat-screen/ChatScreenComponent.tsx";
import AppTabsComponent from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/app-tabs/AppTabsComponent.tsx";
import { AppType } from "@/models/project.model.ts";
import { useIsMobile } from "@/hooks/use-mobile.ts";

import { CommonService } from "../../../../../services";

interface UniversalAppBuilderComponentProps {
    onExitChat: () => void;
    onSupobaseToolOpen: () => void;
    currentAppConfiguration: string[];
}

// Layout function to hide/show content without re-rendering components
const mobileTabLayout = (
    children: React.ReactNode,
    activeTab: string,
    currentTab: string
) => {
    return (
        <div
            style={{
                display: activeTab === currentTab ? "flex" : "none",
                height: "100%",
                width: "100%",
            }}
            className={`mobile-tab-content ${currentTab}`}
        >
            {children}
        </div>
    );
};

const UniversalAppBuilderComponent = (props: UniversalAppBuilderComponentProps) => {
    const { onExitChat, onSupobaseToolOpen, currentAppConfiguration } = props;
    const queryParams = useParams();
    const isMobile = useIsMobile();
    const [activeMobileTab, setActiveMobileTab] = useState("chat");
    const [isChatCollapsed, setIsChatCollapsed] = useState(false);

    useEffect(() => {
        const sub = CommonService._communications.switchToChatTabSubject.subscribe(() => {
            if (isMobile) {
                setActiveMobileTab("chat");
            }
        });
        return () => {
            sub.unsubscribe();
        };
    }, [isMobile]);

    // Manual switch to preview tab when user clicks "View Preview" button on mobile
    useEffect(() => {
        const previewSub = CommonService._communications.setPreviewLoadingStateSubject.subscribe(() => {
            if (isMobile) {
                setActiveMobileTab("preview");
            }
        });
        return () => {
            previewSub.unsubscribe();
        };
    }, [isMobile]);

    const projectId = queryParams.projectId || "";
    const location = useLocation();
    const appType: AppType = useMemo(() => commonService.getAppType(location.pathname), [location.pathname]);

    const handleCollapseChat = useCallback(() => {
        if (!isMobile) {
            setIsChatCollapsed(!isChatCollapsed);
        }
    }, [isChatCollapsed, isMobile]);

    const handleTabChange = useCallback((newValue: string) => {
        setActiveMobileTab(newValue);
        CommonService.handleMixpanelTrack(`${newValue} tab clicked in Mobile`, {
            tab: newValue,
            project_id: projectId,
            app_type: appType,
        });
    }, [projectId, appType]);

    return (
        <motion.div
            key="app-builder-panel-group"
            initial={{ opacity: 0, x: -100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ duration: 0.5 }}
            className="panel-group-animation"
        >
            {isMobile ? (
                <div className="mobile-tabs-container">

                    {/* The content area - it will grow and scroll. */}
                    <div className="mobile-content-area">
                        {mobileTabLayout(
                            <ChatScreenComponent
                                projectId={projectId}
                                appType={appType}
                                onExitChat={onExitChat}
                                onCollapseChat={() => { }} // Disabled for mobile
                            />,
                            activeMobileTab,
                            "chat"
                        )}
                        {mobileTabLayout(
                            <AppTabsComponent
                                appType={appType}
                                projectId={projectId}
                                onSupobaseToolOpen={onSupobaseToolOpen}
                                currentAppConfiguration={currentAppConfiguration}
                                isChatCollapsed={false} // Always false for mobile
                                onExpandChat={() => { }} // Disabled for mobile
                            />,
                            activeMobileTab,
                            "preview"
                        )}
                    </div>

                    <div className="grid w-full grid-cols-2 h-auto mobile-tab-list bg-[#1e1e1e] p-1 rounded-lg m-2 border border-[#2e2e2e] w-[80%] mx-auto">
                        <button
                            onClick={() => handleTabChange("chat")}
                            className={`flex h-7 items-center justify-center rounded-md px-3 py-1 text-xs font-medium transition-all ${activeMobileTab === 'chat' ? 'bg-primary text-primary-foreground shadow-sm font-semibold' : 'text-[#888] hover:text-[#aaa]'}`}
                        >
                            Chat
                        </button>
                        <button
                            onClick={() => handleTabChange("preview")}
                            className={`flex h-7 items-center justify-center rounded-md px-3 py-1 text-xs font-medium transition-all ${activeMobileTab === 'preview' ? 'bg-primary text-primary-foreground shadow-sm font-semibold' : 'text-[#888] hover:text-[#aaa]'}`}
                        >
                            Preview
                        </button>
                    </div>
                </div>
            ) : (
                <PanelGroup direction="horizontal" className="panel-group">
                    <Panel
                        defaultSize={35}
                        minSize={30}
                        style={{
                            display: isChatCollapsed ? 'none' : ''
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.5, ease: "easeInOut" }}
                            className={"panel-content-wrapper"}>
                            <ChatScreenComponent
                                projectId={projectId}
                                appType={appType}
                                onExitChat={onExitChat}
                                onCollapseChat={handleCollapseChat}
                            />
                        </motion.div>
                    </Panel>
                    {!isChatCollapsed && (
                        <PanelResizeHandle className={"panel-resize-handle"} />
                    )}
                    <Panel minSize={50} defaultSize={isChatCollapsed ? 100 : 65}>
                        <AppTabsComponent
                            appType={appType}
                            projectId={projectId}
                            onSupobaseToolOpen={onSupobaseToolOpen}
                            currentAppConfiguration={currentAppConfiguration}
                            isChatCollapsed={isChatCollapsed}
                            onExpandChat={handleCollapseChat}
                        />
                    </Panel>
                </PanelGroup>
            )}
        </motion.div>
    );
};

export default UniversalAppBuilderComponent;